import { Component, computed, inject, InjectionToken, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { GAME_ID } from '../global/game-id';
import { FormConfig } from '../admin/config/base-form.config';
import { AppEvent, CoOrganiserDetail } from './models';
import { EventService } from './event.service';
import { UserService } from '../global/userService/user-service';

export const EVENT_FORM_CONFIG = new InjectionToken<Observable<FormConfig>>('EVENT_FORM_CONFIG');

@Component({
    standalone: false,
    selector: 'abs-event-create',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.CREATE_TITLE' | translate">
            @if (organiserDisplayName()) {
                <ion-item lines="none">
                    <ion-label>
                        <h3>{{ 'GLOBAL.EVENTS.DETAIL.ORGANISER' | translate }}</h3>
                        <p>{{ organiserDisplayName() }}</p>
                    </ion-label>
                </ion-item>
            } @else {
                <ion-item lines="none" color="warning">
                    <ion-label class="ion-text-wrap">
                        <p>{{ 'GLOBAL.EVENTS.SET_DISPLAY_NAME_PROMPT' | translate }}</p>
                    </ion-label>
                    <ion-button slot="end" size="small" (click)="promptSetDisplayName()">
                        {{ 'GLOBAL.EVENTS.SET_DISPLAY_NAME_BUTTON' | translate }}
                    </ion-button>
                </ion-item>
            }

            @if (formConfig$ | async; as formConfig) {
                <abs-form [config]="formConfig" (formSubmit)="onSubmit($event.formValue)">
                    <abs-co-organiser-picker
                        [coOrganiserDetails]="coOrganiserDetails"
                        (coOrganisersChange)="coOrganiserIds = $event"
                    ></abs-co-organiser-picker>

                    @if (saveError) {
                        <ion-item color="danger" lines="none">
                            <ion-label class="ion-text-wrap">{{ saveError }}</ion-label>
                        </ion-item>
                    }

                    <ion-button type="submit" [disabled]="!organiserDisplayName()">
                        {{ 'GLOBAL.EVENTS.SAVE_DRAFT' | translate }}
                    </ion-button>
                </abs-form>
            }
        </abs-page-layout>
    `
})
export class EventCreatePage implements OnInit {
    private eventService = inject(EventService);
    private userService = inject(UserService);
    private alertCtrl = inject(AlertController);
    private translate = inject(TranslateService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    gameId = inject(GAME_ID);
    formConfig$ = inject(EVENT_FORM_CONFIG);

    organiserDisplayName = computed(() => this.userService.user()?.displayName);

    coOrganiserDetails: CoOrganiserDetail[] = [];
    coOrganiserIds: string[] = [];
    saveError: string | null = null;

    ngOnInit() {}

    onSubmit(formValue: Partial<AppEvent>) {
        this.saveError = null;
        const event: Partial<AppEvent> = {
            ...formValue,
            gameId: this.gameId,
            publishStatus: 'draft',
            coOrganisers: this.coOrganiserIds
        };
        this.eventService.createEvent(event).subscribe({
            next: (created) => {
                this.router.navigate(['..', created._id], { relativeTo: this.route });
            },
            error: (err) => {
                this.saveError = err?.error?.message || 'An error occurred while saving. Please check all fields and try again.';
            }
        });
    }

    async promptSetDisplayName() {
        const alert = await this.alertCtrl.create({
            header: this.translate.instant('GLOBAL.EVENTS.SET_DISPLAY_NAME_HEADER'),
            inputs: [
                {
                    name: 'displayName',
                    type: 'text',
                    placeholder: this.translate.instant('GLOBAL.EVENTS.DISPLAY_NAME_PLACEHOLDER')
                }
            ],
            buttons: [
                { text: this.translate.instant('GLOBAL.CANCEL'), role: 'cancel' },
                {
                    text: this.translate.instant('GLOBAL.SAVE'),
                    handler: (data) => {
                        if (data.displayName?.trim()) {
                            this.userService.updateDisplayName(data.displayName.trim()).subscribe(() => {
                                this.userService.refreshAccessToken();
                            });
                        }
                    }
                }
            ]
        });
        await alert.present();
    }
}
